import { Badge, type BadgeTone } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Estados canónicos del sistema. Si un estado del dominio no está acá, mapealo
 * al que más se le parezca antes de inventar un tono nuevo: el color es del
 * ESTADO, no de la pantalla.
 */
export type EstadoDS =
  | "subido"
  | "activo"
  | "aprobado"
  | "por_vencer"
  | "iniciado"
  | "vencido"
  | "reprobado"
  | "atrasado"
  | "pendiente"
  | "cerrada"
  | "no_aplica"
  | "listo"
  | "generando"
  | "fallido"
  /** Programa con temáticas programadas, ninguna enviada aún — mitad
   * frontend-only del split de `activo` (design D4,
   * sdd/capacitaciones-pantalla-unica): "activo" sigue siendo el valor real
   * del accessor del backend, esto es sólo lectura fina en la UI. */
  | "programado"
  | "en_curso"
  | "finalizado"
  | "borrador"
  | "archivado"
  | "programada"
  | "completo"
  | "sin_abrir"
  | "expirado"
  | "no_asignado";

const TONO: Record<EstadoDS, BadgeTone> = {
  subido: "success",
  activo: "success",
  aprobado: "success",
  por_vencer: "warning",
  iniciado: "warning",
  vencido: "destructive",
  reprobado: "destructive",
  atrasado: "destructive",
  pendiente: "neutral",
  cerrada: "neutral",
  no_aplica: "muted",
  listo: "info",
  generando: "brand",
  /** Reusa el tono de "vencido"/"reprobado": es una falla que pide acción del
   * usuario, no un estado neutro de espera. */
  fallido: "destructive",
  /** Mismo tono que "en_curso"/"programada": ambos son estados de tránsito
   * en positivo, no una alerta — se distinguen por la etiqueta, no el color. */
  programado: "brand",
  en_curso: "brand",
  finalizado: "navy",
  borrador: "neutral",
  archivado: "muted",
  /** Coherente con el calendario del Plan de envíos (design #1399, decisión 6). */
  programada: "brand",
  /** Resultado de asignación (Pantalla 4, Ola 8): sin nota ni "reprobado"
   * (decisión #1394). "completo" reusa el tono de "aprobado"/"activo". */
  completo: "success",
  sin_abrir: "neutral",
  expirado: "destructive",
  /** Quinto estado de cobertura por trabajador (design #1448, decisión 1):
   * "no existe la fila de asignación". Tono `muted` como `no_aplica`/
   * `archivado` — a propósito distinto de `neutral` (`sin_abrir`), para que
   * la diferencia también sea visual. */
  no_asignado: "muted",
};

export interface StatusBadgeProps {
  estado: EstadoDS;
  /** Texto visible. Por defecto usa la etiqueta canónica del estado. */
  children?: React.ReactNode;
  /**
   * Punto de 5px a la izquierda. Va prendido salvo que el badge sea el ÚNICO
   * de su fila y no compita con nada: ahí el punto sólo gasta 10px de un
   * ancho que el texto necesita ("Sin programar" mide 92.5px sin él y 102.5
   * con él). El color de fondo ya distingue el estado; el punto ayuda a
   * separar badges vecinos, no a leer uno solo.
   */
  punto?: boolean;
  className?: string;
}

const ETIQUETA: Record<EstadoDS, string> = {
  subido: "Subido",
  activo: "Activo",
  aprobado: "Aprobado",
  por_vencer: "Por vencer",
  iniciado: "Iniciado",
  vencido: "Vencido",
  reprobado: "Reprobado",
  atrasado: "Atrasado",
  pendiente: "Pendiente",
  cerrada: "Cerrada",
  no_aplica: "No aplica",
  listo: "Listo",
  generando: "Generando",
  fallido: "Falló",
  programado: "Programado",
  en_curso: "En curso",
  finalizado: "Finalizado",
  borrador: "Borrador",
  archivado: "Archivado",
  programada: "Programada",
  completo: "Completó",
  sin_abrir: "Sin abrir",
  expirado: "Expirado",
  /** Vocabulario del usuario, no el enum: distingue "no le llegó" de "no la abrió" (sin_abrir). */
  no_asignado: "No le llegó",
};

/**
 * Badge de estado del Design System: fondo suave + texto saturado del mismo
 * tono, con punto de 5px a la izquierda. `generando` late.
 */
export function StatusBadge({
  estado,
  children,
  punto = true,
  className,
}: StatusBadgeProps) {
  return (
    <Badge
      variant={TONO[estado]}
      dot={punto}
      pulse={estado === "generando"}
      className={cn(className)}
    >
      {children ?? ETIQUETA[estado]}
    </Badge>
  );
}
